<script lang="ts" setup>
import type { IRTHeaderImpactLink } from "./IRTHeaderImpactLink";
import { RTImpactLinkTitleEnum } from "~/enums";

const props = defineProps<IRTHeaderImpactLink>();

const config = useRuntimeConfig();
const terminology = useTerminologyStore();
const mainStore = useMain();
const route = useRoute();

const isDownloadButton = computed((): boolean => {
  const closedButtonTitles = [
    RTImpactLinkTitleEnum.DOWNLOAD_PDF,
    RTImpactLinkTitleEnum.DOWNLOAD_EPUB
  ];
  return (props.title && closedButtonTitles.includes(props.title)) || false;
});

const isSubmitManuscriptButton = computed((): boolean => {
  return props.title === RTImpactLinkTitleEnum.SUBMIT_MANUSCRIPT;
});
const toggleSubmitManuscript = inject("toggleSubmitManuscript") as Ref<boolean>;

const linkClasses = computed((): string => {
  let classes: string = useAttrs()?.class as string;

  if (props.iconTitle === "arrowRight")
    classes += " HeaderImpactLink--withArrowRight";

  if (isDownloadButton.value) classes += " HeaderImpactLink--download";
  else classes += " HeaderImpactLink--closedButton";

  return classes;
});

const toggleSubmitManuscriptDialog = (): void => {
  toggleSubmitManuscript.value = !toggleSubmitManuscript.value;
};

onMounted(() => {
  if (mainStore.getIsSubmitManuscriptEnabled) {
    const { submitManuscriptVisible } = route.query;
    if (isSubmitManuscriptButton && submitManuscriptVisible === "true") {
      toggleSubmitManuscript.value = true;
    }
  }
});
</script>

<template>
  <NuxtLink
    v-if="linkToRedirect && linkToRedirect !== 'button'"
    class="HeaderImpactLink"
    :class="linkClasses"
    :to="linkToRedirect"
    v-bind="isDownloadButton && { download: 'true' }"
    :target="target">
    <span>{{ title }}</span>
  </NuxtLink>

  <button
    v-if="linkToRedirect === 'button' && iconTitle !== 'settingsIcon'"
    class="HeaderImpactLink"
    :class="linkClasses"
    @click="toggleSubmitManuscriptDialog()">
    {{ title }}
  </button>

  <NuxtLink
    v-if="iconTitle === 'settingsIcon'"
    :title="`Manage ${terminology.getTerm('topicTitle')}`"
    class="SettingsIcon"
    :class="useAttrs()?.class"
    :to="`https://research-topic-management-app.${config.public.domain}/manage/${route.params.rtId}`"
    target="_blank">
  </NuxtLink>
</template>

<style lang="scss" scoped>
.HeaderImpactLink {
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  justify-content: center;
  align-items: center;
  gap: 9px;
  color: inherit;
  @include f-16-500;

  &--withArrowRight {
    &::after {
      font-family: $fontIcon;
      content: "\e90b" !important;
      margin: 0;
    }
  }

  @media #{$break-md} {
    height: 48px;
    padding: 0 32px;
    @include f-14-500;
  }

  @media #{$break-laptop-sm} {
    padding: 0 40px;
  }

  @media #{$break-sm} {
    padding: 0 37.5px;
  }

  @media #{$break-xs} {
    @include f-14-500;
  }

  @media #{$break-xxs} {
    @include f-12-500;
  }
}

a.HeaderImpactLink svg {
  margin-right: 1px;
}

a.HeaderImpactLink svg {
  margin-right: 1px;
}

.SettingsIcon.MainHeader__link {
  display: flex;
  flex: initial;
  align-items: center;
  justify-content: center;
  padding: 0 55px;
  width: 80px;

  &::after {
    font-family: $fontIcon;
    content: "\e932" !important;
    font-size: 32px;
    margin: 0 auto;

    @media #{$break-xs} {
      font-size: 24px;
    }
  }

  & > svg {
    width: 32px;
  }

  @media #{$break-laptop-sm} {
    padding: 0;
    width: 56px;

    & > svg {
      width: 24px;
    }
  }

  @media #{$break-xs} {
    min-width: 48px;
    flex-basis: 48px;
    flex-grow: 1;
  }
}

a.HeaderImpactLink svg {
  margin-right: 1px;
}
</style>
